type OptimizeExperiments = Readonly<{
  [experiment_name: string]: Readonly<{
    id: string;
    groups: Readonly<string[]>;
  }>;
}>;

type OptimizeBucketObject = Readonly<{
  [bucket_number: number]: string;
}>;

type OptimizeBuckets = OptimizeBucketObject | string[];

function setDifference<T>(a: Set<T>, b: Set<T>): T[] {
  const diff = new Set(a);
  Array.from(b.values()).forEach((elem) => diff.delete(elem));
  return Array.from(diff);
}

/**
 * Validates that all experiment groups defined in OPTIMIZE_EXPERIMENTS
 * have corresponding key-values in OPTIMIZE_BUCKETS
 */
export default function validateExperimentConfig(
  experiments: OptimizeExperiments,
  buckets: OptimizeBuckets
): void {
  const actualBuckets = new Set(
    Array.isArray(buckets) ? buckets : Array.from(Object.values(buckets))
  );
  const expectedBuckets: Set<string> = new Set();
  const sharedBuckets: string[] = [];
  Object.entries(experiments).forEach(([experimentName, def]) => {
    def.groups.forEach((group) => {
      // Allow for shared controls
      if (actualBuckets.has(group)) {
        sharedBuckets.push(group);
      } else {
        // Allow for shared controls
        expectedBuckets.add(`${experimentName}.${group}`);
      }
    });
  });
  sharedBuckets.forEach((bucket) => {
    actualBuckets.delete(bucket);
  });
  const missing = setDifference(expectedBuckets, actualBuckets);
  const extra = setDifference(actualBuckets, expectedBuckets);
  const errs: string[] = [];
  const OE = "OPTIMIZE_EXPERIMENTS";
  const OB = "OPTIMIZE_BUCKETS";
  if (missing.length) {
    errs.push(
      `groups defined in ${OE} but missing from ${OB}: ${missing.join(", ")}`
    );
  }
  if (extra.length) {
    errs.push(
      `groups in ${OB} without definitions in ${OE}: ${extra.join(", ")}`
    );
  }
  if (errs.length) {
    throw new Error(`AB Tests misconfigured: ${errs.join("; ")}`);
  }
}
